export const EVENT_KIND_ORDER = Object.freeze([
  "fishingCatch",
  "characterSessionStarted",
  "characterSessionObserved",
  "characterSessionEnded",
]);

export const AVAILABILITY = Object.freeze({
  localOnly: Object.freeze({ label: "Local", className: "badge-ghost" }),
  localAndOnline: Object.freeze({ label: "Local + online", className: "badge-success badge-soft" }),
  onlineCache: Object.freeze({ label: "Online cache", className: "badge-info badge-outline" }),
  online: Object.freeze({ label: "Online", className: "badge-info badge-outline" }),
});

export const CATCH_LOG_ACTIVITIES = Object.freeze(["fishing"]);
export const CATCH_LOG_CHARACTER_IDENTITY_STATES = Object.freeze([
  "exactCharacter", "unresolvedActivity",
]);
export const CATCH_LOG_IDENTITY_CONFIDENCE = Object.freeze([
  "userConfirmed", "sourceObserved", "uninterruptedSession", "sourceActorAssociation", "unresolved",
]);
export const CATCH_LOG_SOURCE_STATES = Object.freeze([
  "connecting", "catchingUp", "ready", "warning", "failed",
]);
export const CATCH_LOG_ITEM_GRADE_TONES = Object.freeze([
  "unknown", "white", "green", "blue", "yellow", "red",
]);

const MAX_ROWS = 400;
const MAX_CANDIDATES = MAX_ROWS * 4;
const MAX_OUTCOMES = 4_096;
const MAX_COOCCURRENCES = 100;
const MAX_FILTER_OPTIONS = 400;
const I64_MIN = -(2n ** 63n);
const I64_MAX = 2n ** 63n - 1n;
const U64_MAX = 2n ** 64n - 1n;
const UTF8 = new TextEncoder();

function exactFields(value, fields) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = fields.slice().sort();
  return actual.length === expected.length
    && actual.every((field, index) => field === expected[index]);
}

function boundedGraphic(value, maximumBytes) {
  return typeof value === "string"
    && value.length > 0
    && value.length <= maximumBytes
    && [...value].every((character) => {
      const code = character.codePointAt(0);
      return code === 0x20 || (code >= 0x21 && code <= 0x7e);
    });
}

function boundedLabel(value, maximumBytes = 256) {
  return typeof value === "string"
    && value.length > 0
    && UTF8.encode(value).byteLength <= maximumBytes
    && !/\p{Cc}/u.test(value);
}

function unsignedCount(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function decimalInteger(value, minimum, maximum, signed) {
  const pattern = signed ? /^(?:0|-[1-9][0-9]*|[1-9][0-9]*)$/u : /^(?:0|[1-9][0-9]*)$/u;
  if (typeof value !== "string" || !pattern.test(value)) return false;
  const decoded = BigInt(value);
  return decoded >= minimum && decoded <= maximum;
}

function i64Decimal(value) {
  return decimalInteger(value, I64_MIN, I64_MAX, true);
}

function u64Decimal(value) {
  return decimalInteger(value, 0n, U64_MAX, false);
}

function validSourceSessionIdentity(value, kind) {
  const sessionEvent = kind !== "fishingCatch";
  if (!sessionEvent) return value === null;
  if (exactFields(value, ["state"]) && value.state === "unresolved") return true;
  return exactFields(value, ["state", "serviceRegion", "familyId", "characterId"])
    && value.state === "exactCharacter"
    && /^(?:[A-Z0-9]{1,16})$/u.test(value.serviceRegion)
    && /^(?:[0-9]{1,32})$/u.test(value.familyId)
    && /^(?:[0-9]{1,32})$/u.test(value.characterId);
}

function compareUtf8(left, right) {
  const leftBytes = UTF8.encode(left);
  const rightBytes = UTF8.encode(right);
  const length = Math.min(leftBytes.length, rightBytes.length);
  for (let index = 0; index < length; index += 1) {
    if (leftBytes[index] !== rightBytes[index]) return leftBytes[index] - rightBytes[index];
  }
  return leftBytes.length - rightBytes.length;
}

function strictlySortedByValue(options) {
  return options.every((option, index) => index === 0 || options[index - 1].value < option.value);
}

function validFilterOptions(options) {
  return Array.isArray(options)
    && options.length <= MAX_FILTER_OPTIONS
    && options.every((option) => (
    exactFields(option, ["value", "label", "retainedEvents"])
    && boundedGraphic(option.value, 128)
    && boundedLabel(option.label)
    && unsignedCount(option.retainedEvents)
    && option.retainedEvents > 0
    ))
    && strictlySortedByValue(options);
}

function validFamilyFilterOptions(options, characters) {
  return Array.isArray(options)
    && Array.isArray(characters)
    && options.length <= MAX_FILTER_OPTIONS
    && options.every((option) => (
      exactFields(option, ["value", "label", "retainedEvents"])
      && boundedGraphic(option.value, 128)
      && boundedLabel(option.label)
      && unsignedCount(option.retainedEvents)
      && (option.retainedEvents > 0 || characters.some((character) => (
        character.currentlyInferred && character.familyValue === option.value
      )))
    ))
    && strictlySortedByValue(options);
}

function validCharacterFilterOptions(options, families) {
  const familyValues = new Set(families.map((option) => option.value));
  return Array.isArray(options)
    && options.length <= MAX_FILTER_OPTIONS
    && options.every((option) => (
    exactFields(option, [
      "value", "label", "familyValue", "retainedEvents", "identityState",
      "identityConfidence", "characterClassKey", "currentlyInferred", "activitySummaries",
    ])
    && boundedGraphic(option.value, 128)
    && boundedLabel(option.label)
    && boundedGraphic(option.familyValue, 128)
    && familyValues.has(option.familyValue)
    && unsignedCount(option.retainedEvents)
    && typeof option.currentlyInferred === "boolean"
    && (option.retainedEvents > 0 || option.currentlyInferred)
    && CATCH_LOG_CHARACTER_IDENTITY_STATES.includes(option.identityState)
    && CATCH_LOG_IDENTITY_CONFIDENCE.includes(option.identityConfidence)
    && ((option.identityState === "unresolvedActivity")
      === (option.identityConfidence === "unresolved"))
    && (option.characterClassKey === null
      || (Number.isInteger(option.characterClassKey) && option.characterClassKey > 0))
    && (option.identityState !== "unresolvedActivity" || option.characterClassKey === null)
    && Array.isArray(option.activitySummaries)
    && option.activitySummaries.length <= 3
    && option.activitySummaries.every((summary) => (
      exactFields(summary, ["activity", "retainedEvents", "latestSourceObservedAtNs"])
      && CATCH_LOG_ACTIVITIES.includes(summary.activity)
      && unsignedCount(summary.retainedEvents)
      && summary.retainedEvents > 0
      && i64Decimal(summary.latestSourceObservedAtNs)
    ))
    && option.activitySummaries.every((summary, index) => (
      index === 0 || CATCH_LOG_ACTIVITIES.indexOf(option.activitySummaries[index - 1].activity)
        < CATCH_LOG_ACTIVITIES.indexOf(summary.activity)
    ))
    && option.activitySummaries.reduce(
      (total, summary) => total + BigInt(summary.retainedEvents),
      0n,
    ) === BigInt(option.retainedEvents)
    ))
    && strictlySortedByValue(options);
}

export function validateCatchLogFilters(value) {
  if (!exactFields(value, ["families", "characters", "locations"])
    || !validFamilyFilterOptions(value.families, value.characters)
    || !validCharacterFilterOptions(value.characters, value.families)
    || !validFilterOptions(value.locations)) {
    throw new TypeError("Unsupported catch-log filter contract.");
  }
  return value;
}

export function validateCatchLogSlice(value) {
  if (!exactFields(value, [
    "schemaVersion", "projectionVersion", "rows", "statistics", "outcomes",
    "cooccurrences", "filters", "candidateRows",
  ]) || value.schemaVersion !== 1
    || !unsignedCount(value.projectionVersion) || value.projectionVersion > 0xffff_ffff
    || !unsignedCount(value.candidateRows) || value.candidateRows > MAX_CANDIDATES
    || !Array.isArray(value.rows) || value.rows.length > MAX_ROWS
    || !Array.isArray(value.outcomes) || value.outcomes.length > MAX_OUTCOMES
    || !Array.isArray(value.cooccurrences) || value.cooccurrences.length > MAX_COOCCURRENCES
    || !exactFields(value.statistics, [
      "eventCount", "catchCount", "outcomeCount",
      "eligibleCatchCount", "provenOutcomeCount", "excludedCompositionCatchCount",
      "omittedUnprovenOutcomeRows",
      "firstSourceObservedAtNs", "lastSourceObservedAtNs",
    ]) || !exactFields(value.filters, ["families", "characters", "locations"])
    || ![
      value.statistics.eventCount,
      value.statistics.catchCount,
      value.statistics.outcomeCount,
      value.statistics.eligibleCatchCount,
      value.statistics.provenOutcomeCount,
      value.statistics.excludedCompositionCatchCount,
      value.statistics.omittedUnprovenOutcomeRows,
    ].every(unsignedCount)
    || (value.statistics.firstSourceObservedAtNs !== null
      && !i64Decimal(value.statistics.firstSourceObservedAtNs))
    || (value.statistics.lastSourceObservedAtNs !== null
      && !i64Decimal(value.statistics.lastSourceObservedAtNs))) {
    throw new TypeError("Unsupported catch-log slice contract.");
  }
  validateCatchLogFilters(value.filters);
  for (const row of value.rows) {
    if (!exactFields(row, [
      "eventId", "sourceSequence", "localSourceSequence", "sourceObservedAtNs", "availability", "kind",
      "identityConfidence",
      "familyFilterKey", "familyLabel", "characterFilterKey", "characterLabel",
      "sourceSessionIdentity", "locationFilterKey", "locationLabel", "summary", "outcomes",
    ]) || !boundedGraphic(row.eventId, 128)
      || !u64Decimal(row.sourceSequence)
      || (row.localSourceSequence !== null && !u64Decimal(row.localSourceSequence))
      || !i64Decimal(row.sourceObservedAtNs)
      || !Object.hasOwn(AVAILABILITY, row.availability)
      || !EVENT_KIND_ORDER.includes(row.kind)
      || !CATCH_LOG_IDENTITY_CONFIDENCE.includes(row.identityConfidence)
      || !boundedGraphic(row.familyFilterKey, 128)
      || !boundedLabel(row.familyLabel)
      || !boundedGraphic(row.characterFilterKey, 128)
      || !boundedLabel(row.characterLabel)
      || !validSourceSessionIdentity(row.sourceSessionIdentity, row.kind)
      || (row.locationFilterKey !== null && !boundedGraphic(row.locationFilterKey, 128))
      || (row.locationLabel !== null && !boundedLabel(row.locationLabel))
      || !boundedLabel(row.summary)
      || !Array.isArray(row.outcomes)
      || row.outcomes.length > 8
      || (row.kind !== "fishingCatch" && row.outcomes.length !== 0)
      || !row.outcomes.every((outcome) => (
        exactFields(outcome, ["label", "iconPath", "gradeTone"])
        && boundedLabel(outcome.label)
        && (outcome.iconPath === null || (
          boundedGraphic(outcome.iconPath, 512)
          && outcome.iconPath.startsWith("/images/")
          && !outcome.iconPath.includes("..")
          && !outcome.iconPath.includes("?")
          && !outcome.iconPath.includes("#")
        ))
        && CATCH_LOG_ITEM_GRADE_TONES.includes(outcome.gradeTone)
      ))) {
      throw new TypeError("Unsupported catch-log row contract.");
    }
  }
  if (value.rows.some((row, index) => {
    if (index === 0) return false;
    const prior = value.rows[index - 1];
    const priorTime = BigInt(prior.sourceObservedAtNs);
    const rowTime = BigInt(row.sourceObservedAtNs);
    return priorTime < rowTime
      || (priorTime === rowTime && BigInt(prior.sourceSequence) <= BigInt(row.sourceSequence));
  })) {
    throw new TypeError("Unsupported catch-log row order.");
  }
  for (const outcome of value.outcomes) {
    if (!exactFields(outcome, [
      "label", "quantityDecimal", "outcomeObservations", "catchOccurrences",
    ]) || !boundedLabel(outcome.label)
      || !/^(?:0|[1-9][0-9]{0,38})$/u.test(outcome.quantityDecimal)
      || !unsignedCount(outcome.outcomeObservations)
      || !unsignedCount(outcome.catchOccurrences)) {
      throw new TypeError("Unsupported catch-log aggregate contract.");
    }
  }
  for (const pair of value.cooccurrences) {
    if (!exactFields(pair, ["leftLabel", "rightLabel", "catchCount"])
      || !boundedLabel(pair.leftLabel) || !boundedLabel(pair.rightLabel)
      || compareUtf8(pair.leftLabel, pair.rightLabel) >= 0
      || !unsignedCount(pair.catchCount)) {
      throw new TypeError("Unsupported catch-log co-occurrence contract.");
    }
  }
  return value;
}

export function validateCatchLogSourceStatus(value) {
  if (!exactFields(value, ["schemaVersion", "state", "label", "detail"])
    || value.schemaVersion !== 1
    || !CATCH_LOG_SOURCE_STATES.includes(value.state)
    || !boundedLabel(value.label)
    || !boundedLabel(value.detail, 1_024)) {
    throw new TypeError("Unsupported catch-log source-status contract.");
  }
  return value;
}
